/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DEFAULT_PAGE_SIZE } from "@/lib/constants/query-config";
import { useUrlState } from "../../state/use-url-state";

import { buildFilterUrlStatePartial } from "./build-filter-url-state-partial";
import type {
  FilterRecord,
  FilterState,
  StandardFiltersConfig,
  StandardFiltersReturn,
  UrlSyncState,
} from "./types";
import { useStandardFilterUrlDerivations } from "./use-standard-filter-url-derivations";
import { useStandardFilterUrlSyncEffect } from "./use-standard-filter-url-sync-effect";

export function useStandardFilters<TFilter extends FilterRecord = FilterRecord>(
  config: StandardFiltersConfig<TFilter>,
): StandardFiltersReturn<TFilter> {
  const { id, pageSize = DEFAULT_PAGE_SIZE, searchDebounceMs = 300 } = config;

  if (!id || typeof id !== "string") {
    throw new Error(
      "useStandardFilters: `id` is required and must be a non-empty string.",
    );
  }

  const {
    shouldSyncWithUrl,
    sanitizedInitialFilters,
    searchKey,
    pageKey,
    pageSizeKey,
    filterKeys,
    filterUrlKeyMap,
    urlKeysArray,
    filterDefaults,
  } = useStandardFilterUrlDerivations(config, id);

  const { state: urlState, setUrlState } = useUrlState<UrlSyncState>(
    urlKeysArray,
    {
      defaults: {
        [searchKey]: "",
        [pageKey]: 1,
        [pageSizeKey]: pageSize,
        ...filterDefaults,
      } as UrlSyncState,
      enabled: shouldSyncWithUrl,
    },
  );

  const initialSearchValue = shouldSyncWithUrl
    ? String(urlState[searchKey] ?? "")
    : "";
  const normalizedInitialSearch = initialSearchValue.trim();
  const initialPageValue = shouldSyncWithUrl
    ? Math.max(1, Number(urlState[pageKey]) || 1)
    : 1;
  const initialPageSizeValue = shouldSyncWithUrl
    ? Math.max(1, Number(urlState[pageSizeKey]) || pageSize)
    : pageSize;

  const initialFilterState = useMemo<FilterState<TFilter>>(() => {
    const base: Record<string, unknown> = { ...sanitizedInitialFilters };
    if (shouldSyncWithUrl) {
      for (const key of filterKeys) {
        const urlKey = filterUrlKeyMap[key];
        if (!urlKey) continue;
        const value = urlState[urlKey];
        if (value !== undefined) {
          base[key] = value;
        }
      }
    }
    return {
      ...base,
      search: normalizedInitialSearch,
      page: initialPageValue,
      pageSize: initialPageSizeValue,
    } as FilterState<TFilter>;
  }, [
    filterKeys,
    filterUrlKeyMap,
    normalizedInitialSearch,
    initialPageValue,
    initialPageSizeValue,
    sanitizedInitialFilters,
    shouldSyncWithUrl,
    urlState,
  ]);

  const [searchTermInput, setSearchTermInput] =
    useState<string>(initialSearchValue);

  const [searchTermServer, setSearchTermServer] = useState<string>(
    normalizedInitialSearch,
  );
  const [page, setPage] = useState(initialPageValue);
  const [pageSizeState, setPageSizeState] = useState(initialPageSizeValue);

  const [filterState, setFilterStateInternal] =
    useState<FilterState<TFilter>>(initialFilterState);

  const hasMountedRef = useRef(false);
  const previousUrlStateRef = useRef<Partial<UrlSyncState>>({});

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousUrlStateRef.current = shouldSyncWithUrl ? { ...urlState } : {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = (searchTermInput || "").trim();
      if (nextSearch === searchTermServer) {
        return;
      }

      setPage(1);
      setSearchTermServer(nextSearch);
      setFilterStateInternal(
        (prev) =>
          ({
            ...prev,
            search: nextSearch,
            page: 1,
          }) as FilterState<TFilter>,
      );
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchTermInput, searchTermServer, searchDebounceMs]);

  useEffect(() => {
    setFilterStateInternal(
      (prev) =>
        ({
          ...prev,
          page,
        }) as FilterState<TFilter>,
    );
  }, [page]);

  useEffect(() => {
    setFilterStateInternal((prev) => {
      if (prev.pageSize === pageSizeState) {
        return prev;
      }

      return {
        ...prev,
        pageSize: pageSizeState,
        page: 1,
      } as FilterState<TFilter>;
    });
  }, [pageSizeState]);

  const filterUrlState = useMemo<Partial<UrlSyncState>>(
    () =>
      buildFilterUrlStatePartial({
        shouldSyncWithUrl,
        filterKeys,
        filterUrlKeyMap,
        filterState: filterState as Record<string, unknown>,
        sanitizedInitialFilters: sanitizedInitialFilters as Record<
          string,
          unknown
        >,
      }),
    [
      shouldSyncWithUrl,
      filterKeys,
      filterUrlKeyMap,
      filterState,
      sanitizedInitialFilters,
    ],
  );

  useStandardFilterUrlSyncEffect({
    shouldSyncWithUrl,
    hasMountedRef,
    previousUrlStateRef,
    searchTermServer,
    page,
    pageSizeState,
    pageSize,
    setUrlState,
    searchKey,
    pageKey,
    pageSizeKey,
    filterUrlState,
    filterKeys,
    filterUrlKeyMap,
  });

  const handleClearAllFilters = () => {
    setSearchTermInput("");
    setSearchTermServer("");
    setPage(1);
    setFilterStateInternal({
      ...sanitizedInitialFilters,
      search: "",
      page: 1,
      pageSize: pageSizeState,
    } as FilterState<TFilter>);
  };

  const handleClearSearch = () => {
    setSearchTermInput("");
    setSearchTermServer("");
    setPage(1);
    setFilterStateInternal(
      (prev) =>
        ({
          ...prev,
          search: "",
          page: 1,
        }) as FilterState<TFilter>,
    );
  };

  const getPaginationInfo = (totalCount: number) => {
    const visibleRangeStart =
      totalCount === 0 ? 0 : (page - 1) * pageSizeState + 1;
    const visibleRangeEnd =
      totalCount === 0 ? 0 : Math.min(page * pageSizeState, totalCount);
    const headerSubtitle = `Showing ${visibleRangeStart}-${visibleRangeEnd} of ${totalCount} items`;

    return {
      visibleRangeStart,
      visibleRangeEnd,
      headerSubtitle,
    };
  };

  const setFilterStateWrapper = (newFilters: Partial<FilterState<TFilter>>) => {
    setFilterStateInternal(
      (prev) =>
        ({
          ...prev,
          ...newFilters,
          ...(!("page" in newFilters) ? { page: 1 } : {}),
        }) as FilterState<TFilter>,
    );

    if (!("page" in newFilters)) {
      setPage(1);
    }

    if ("pageSize" in newFilters && typeof newFilters.pageSize === "number") {
      setPageSizeState(newFilters.pageSize);
    }
  };

  const handleSetPageSize = useCallback((nextPageSize: number) => {
    const validPageSize = Math.max(1, Number(nextPageSize));
    if (!Number.isFinite(validPageSize)) {
      return;
    }
    setPage(1);
    setPageSizeState(validPageSize);
    setFilterStateInternal(
      (prev) =>
        ({
          ...prev,
          page: 1,
          pageSize: validPageSize,
        }) as FilterState<TFilter>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    searchTermInput,
    setSearchTermInput,
    searchTermServer,
    page,
    setPage,
    pageSize: pageSizeState,
    setPageSize: handleSetPageSize,
    filterState,
    setFilterState: setFilterStateWrapper,
    handleClearAllFilters,
    handleClearSearch,
    getPaginationInfo,
  };
}
